import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S2cQuery } from "types";
import { formatDateSafe } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S2cQuery = {
  from_date: startOfYear,
  to_date: todayString,
};

const S2cReport: React.FC = () => {
  const { fetchS2cHkd } = useReport();
  const [filters, setFilters] = useState<S2cQuery>(defaultFilters);
  const [submitted, setSubmitted] = useState<S2cQuery | null>(null);

  const handleFilterChange = useCallback((key: keyof S2cQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS2cHkd.mutate(filters);
    },
    [fetchS2cHkd, filters],
  );

  const responseData = fetchS2cHkd.data?.data;
  const summary = useMemo(() => {
    if (!responseData) {
      return {
        total_revenue: 0,
        total_cost: 0,
        profit_loss: 0,
        profit_margin: 0,
      };
    }
    return {
      total_revenue: responseData.total_revenue || 0,
      total_cost: responseData.total_cost || 0,
      profit_loss: responseData.profit_loss || 0,
      profit_margin: responseData.profit_margin || 0,
    };
  }, [responseData]);

  const columns = useMemo(
    () => [
      {
        headerName: "Ngày hạch toán",
        field: "accounting_date",
        minWidth: 140,
        valueFormatter: (params: any) => formatDateSafe(params.value),
      },
      {
        headerName: "Chứng từ",
        field: "voucher_code",
        minWidth: 140,
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 260,
        flex: 1,
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const isLoading = fetchS2cHkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Báo cáo S2c-HKD</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            Sổ chi tiết doanh thu - chi phí
          </h1>
          <p className="text-sm text-gray-600">
            Lọc theo thời gian. Từ ngày và đến ngày là bắt buộc.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? "Đang tải..." : "Xem báo cáo"}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              📊 Tổng hợp kỳ
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-600">Tổng doanh thu</p>
                <p className="text-xl font-bold text-green-700">
                  {summary.total_revenue.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tổng chi phí</p>
                <p className="text-xl font-bold text-red-700">
                  {summary.total_cost.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Chênh lệch (lãi/lỗ)</p>
                <p
                  className={`text-xl font-bold ${
                    summary.profit_loss >= 0 ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {summary.profit_loss.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tỷ suất lợi nhuận</p>
                <p className="text-xl font-bold text-blue-700">
                  {summary.profit_margin.toFixed(2)} %
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
          </div>

          <AgGridTable
            rows={[]}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S2cReport;
